import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

const useCases = [
  {
    id: 'recruitment-intake',
    industry: 'Recruitment',
    challenge: 'High-volume candidate and client inquiries',
    scenario: 'A recruitment agency receives hundreds of inbound inquiries weekly — candidate applications, client briefs, interview scheduling requests — through email, web forms, and messaging channels.',
    solution: 'An AI intake system that structures incoming requests, extracts key information, routes to appropriate teams, and flags priority cases based on defined criteria.',
    outcomes: [
      'Structured intake across all channels',
      'Automatic prioritization of urgent briefs',
      'Reduced time-to-first-response',
      'Consistent data capture for reporting',
    ],
  },
  {
    id: 'logistics-coordination',
    industry: 'Logistics',
    challenge: 'Fragmented coordination requests',
    scenario: 'A logistics provider handles inbound shipment requests, delivery changes, and status inquiries from multiple clients, each with different formats and urgency levels.',
    solution: 'Automated intake and routing system that normalizes requests, validates required information, and integrates with existing dispatch and tracking workflows.',
    outcomes: [
      'Unified request handling',
      'Automated validation and acknowledgment',
      'Faster dispatch coordination',
      'Clear audit trail for all requests',
    ],
  },
  {
    id: 'operations-processing',
    industry: 'Operations',
    challenge: 'Manual case processing bottlenecks',
    scenario: 'An operations team processes internal service requests, compliance forms, and exception cases — work that requires consistent handling but consumes significant admin time.',
    solution: 'Workflow automation that handles intake, initial triage, routing based on case type, and escalation when human judgment is required.',
    outcomes: [
      'Reduced manual processing time',
      'Consistent handling of routine cases',
      'Clear escalation paths for exceptions',
      'Better visibility into case status',
    ],
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 },
  },
};

export const UseCases = () => {
  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="use-cases"
      data-testid="use-cases-section"
      className="bg-slate-50 py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.5 }}
          className="mb-16 max-w-3xl"
        >
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold mb-4">
            Operational Scenarios
          </p>
          <h2
            data-testid="use-cases-heading"
            className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900 font-['Manrope'] mb-6"
          >
            Where structured automation delivers value
          </h2>
          <p className="text-base md:text-lg text-slate-600 leading-relaxed">
            Real operational contexts where AI intake systems and workflow automation reduce friction and improve consistency.
          </p>
        </motion.div>

        {/* Use Case Cards */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="space-y-8"
        >
          {useCases.map((useCase, index) => (
            <motion.div
              key={useCase.id}
              variants={itemVariants}
              data-testid={`use-case-${useCase.id}`}
              className="bg-white border border-slate-200 rounded-sm overflow-hidden hover:border-slate-300 transition-colors"
            >
              <div className="grid lg:grid-cols-12 gap-0">
                {/* Left: Context */}
                <div className="lg:col-span-5 p-8 md:p-10 border-b lg:border-b-0 lg:border-r border-slate-100">
                  <p className="text-xs uppercase tracking-[0.15em] text-slate-400 font-semibold mb-3">
                    {useCase.industry}
                  </p>
                  <h3 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4 font-['Manrope']">
                    {useCase.challenge}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {useCase.scenario}
                  </p>
                </div>

                {/* Right: Solution & Outcomes */}
                <div className="lg:col-span-7 p-8 md:p-10">
                  <p className="text-xs uppercase tracking-[0.15em] text-slate-400 font-semibold mb-3">
                    Automation Layer
                  </p>
                  <p className="text-base text-slate-700 leading-relaxed mb-6">
                    {useCase.solution}
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {useCase.outcomes.map((outcome, i) => (
                      <span
                        key={i}
                        className="inline-block px-3 py-1.5 bg-slate-100 text-xs text-slate-600 font-medium rounded-sm"
                      >
                        {outcome}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-16 text-center"
        >
          <p className="text-base text-slate-600 mb-6">
            Have a similar operational challenge?
          </p>
          <button
            onClick={scrollToContact}
            data-testid="use-cases-cta"
            className="inline-flex items-center text-sm font-semibold text-slate-900 hover:text-slate-700 transition-colors group"
          >
            Discuss your workflow
            <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={16} strokeWidth={1.5} />
          </button>
        </motion.div>
      </div>
    </section>
  );
};
